import { AddOutlined, CancelOutlined, DeleteOutlineOutlined } from "@mui/icons-material";
import { styled, darken, alpha, lighten } from "@mui/material/styles";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const getBackgroundColor = (color, mode, opacity) => {
  return mode === "dark" ? darken(color, opacity) : lighten(color, opacity);
};

const SyledGrid = styled(DataGrid)(({ theme }) => ({
  "& .status--O": {
    backgroundColor: getBackgroundColor(theme.palette.info.main, theme.palette.mode, 0.7),
    "&:hover": {
      backgroundColor: getBackgroundColor(theme.palette.info.main, theme.palette.mode, 0.6),
    },
    "&.Mui-selected": {
      backgroundColor: getBackgroundColor(theme.palette.primary.main, theme.palette.mode, 0.7),
      "&:hover": {
        backgroundColor: getBackgroundColor(theme.palette.primary.main, theme.palette.mode, 0.6),
      },
    },
  },
  "& .status--R": {
    backgroundColor: getBackgroundColor(theme.palette.success.main, theme.palette.mode, 0.7),
    "&:hover": {
      backgroundColor: getBackgroundColor(theme.palette.success.main, theme.palette.mode, 0.6),
    },
    "&.Mui-selected": {
      backgroundColor: getBackgroundColor(theme.palette.primary.main, theme.palette.mode, 0.7),
      "&:hover": {
        backgroundColor: getBackgroundColor(theme.palette.primary.main, theme.palette.mode, 0.6),
      },
    },
  },
  "& .status--C": {
    backgroundColor: getBackgroundColor(theme.palette.warning.main, theme.palette.mode, 0.7),
    "&:hover": {
      backgroundColor: getBackgroundColor(theme.palette.warning.main, theme.palette.mode, 0.6),
    },
    "&.Mui-selected": {
      backgroundColor: getBackgroundColor(theme.palette.primary.main, theme.palette.mode, 0.7),
      "&:hover": {
        backgroundColor: getBackgroundColor(theme.palette.primary.main, theme.palette.mode, 0.6),
      },
    },
  },
  "& .status--I": {
    backgroundColor: getBackgroundColor(theme.palette.secondary.main, theme.palette.mode, 0.7),
    "&:hover": {
      backgroundColor: getBackgroundColor(theme.palette.secondary.main, theme.palette.mode, 0.6),
    },
    "&.Mui-selected": {
      backgroundColor: getBackgroundColor(theme.palette.primary.main, theme.palette.mode, 0.7),
      "&:hover": {
        backgroundColor: getBackgroundColor(theme.palette.primary.main, theme.palette.mode, 0.6),
      },
    },
  },
}));

const StripedGrid = styled(DataGrid)(({ theme }) => ({
  "& .even": {
    backgroundColor: theme.palette.grey[200],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.info.main, 0.2),
      "@media (hover:none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(theme.palette.info.main, 0.2 + theme.palette.action.selectedOpacity),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(
          theme.palette.info.main,
          0.2 + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
        ),
        "@media (hover:none)": {
          backgroundColor: alpha(
            theme.palette.info.main,
            0.2 + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
  "& .odd": {
    backgroundColor: "transparent",
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      "@media (hover:none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        0.2 + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
      ),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(theme.palette.primary.main, 0.2),
        "@media (hover:none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            0.2 + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}));

function StyledDataGrid({ ...data }) {
  return (
    <SyledGrid
      autoHeight
      showCellVerticalBorder
      showColumnVerticalBorder
      {...data}
      disableColumnMenu={true}
    />
  );
}

function StripedDataGrid({ ...data }) {
  return (
    <StripedGrid
      autoHeight
      showCellVerticalBorder
      showColumnVerticalBorder
      {...data}
      disableColumnMenu={true}
    />
  );
}

function StripedCheckBoxDataGrid({ ...data }) {
  return (
    <StripedGrid
      autoHeight
      showCellVerticalBorder
      showColumnVerticalBorder
      {...data}
      disableColumnMenu={true}
      checkboxSelection={true}
      disableRowSelectionOnClick={true}
    />
  );
}

const DataGridLabel = () => (
  <MDBox display="flex" alignItems="center">
    <MDTypography pb={1} color="info" variant="h5" mr="2rem">
      Datagrid Display
    </MDTypography>
    <MDTypography pb={1} color="secondary" variant="h6" mr="2rem">
      Row Status:
    </MDTypography>
    <MDTypography pb={1} color="info" variant="h6" mr="2rem">
      Open
    </MDTypography>
    <MDTypography pb={1} color="warning" variant="h6" mr="2rem">
      Cancelled
    </MDTypography>
    <MDTypography pb={1} color="primary" variant="h6">
      Selected
    </MDTypography>
  </MDBox>
);

const CancelAction = ({ onClick, disabled }) => (
  <GridActionsCellItem
    icon={<CancelOutlined />}
    title="cancel"
    disabled={disabled}
    color="error"
    label="cancel"
    onClick={onClick}
  />
);

const AddAction = ({ onClick, disabled }) => (
  <GridActionsCellItem
    icon={<AddOutlined />}
    title="add"
    disabled={disabled}
    color="info"
    label="add"
    onClick={onClick}
  />
);

const DelAction = ({ onClick }) => (
  <GridActionsCellItem
    icon={<DeleteOutlineOutlined />}
    title="delete"
    color="error"
    label="delete"
    onClick={onClick}
  />
);

export { StyledDataGrid, StripedDataGrid,StripedCheckBoxDataGrid, CancelAction,AddAction, DelAction, DataGridLabel };
